export type NonNullable<T> = Exclude<T, null | undefined>;

/**
 * Filters out empty values from an array
 * Example usage: [1, undefined, 2, 3, null].filter(notEmpty) --> [1, 2, 3]
 * @param value
 */
export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
	return value !== null && value !== undefined;
}

export const arrayOfAll =
	<T>() =>
	<U extends T[]>(array: U & ([T] extends [U[number]] ? unknown : 'Invalid')) =>
		array;

type ExtendDiscriminatedUnion<
	T extends Record<PropertyKey, any>,
	K extends keyof T,
	M extends Partial<Record<T[K], object>>,
> = T extends unknown
	? { [P in T[K]]: (x: M[P] extends object ? M[P] : unknown) => void }[T[K]] extends (x: infer I) => void
		? {
				[P in keyof (T & I)]: (T & I)[P];
			}
		: never
	: never;

/**
 * Extends an existing discriminated union type with additional properties
 */
export type ExtendUnion<
	Union extends { type: string },
	Extension extends Partial<Record<Union['type'], object>>,
> = ExtendDiscriminatedUnion<Union, 'type', Extension>;

/**
 * Ensures that a value is defined and not null or undefined
 * @param value
 *
 * @throws Error if value is undefined or null
 */
export function ensureDefined<T>(value: T | undefined | null): T {
	if (value === undefined || value === null) {
		throw new NloUnknownError({ message: 'Value should be defined' });
	}

	return value;
}
